import { ref, computed, nextTick, onBeforeUnmount, onMounted } from 'vue';
import type { TMessage } from '@/models/CopilotChat';
import type CopilotService from '@/infrastructure/services/copilot.service';
import { createCopilotService } from '@/infrastructure/services/copilotServiceFactory';
import { getMessages, clearAllMessages } from '@/infrastructure/idb/indexedDb';
import { saveMessages } from '@/infrastructure/idb/indexedDb';
import { copyMessage as copyToClipboard, notification } from '@/shared/composables/useCopyMessage';
import { communication } from '@/infrastructure/plugins/communication';
import { renderMarkdown, htmlToWhatsApp } from '@/shared/utils/markdownRenderer';

export const useCopilotChat = (
  threadId: string,
  user_email: string,
  requester_id: string,
  agentId: string,
  reference_id: string | undefined,
  instance: any,
  stream: boolean
) => {
  const isOpen = ref(false);
  const messages = ref<TMessage[]>([]);
  const loading = ref(false);
  const loadingSendingMessage = ref(false);
  const error = ref<string | undefined>(undefined);
  const currentThreadId = ref<string | undefined>(undefined);
  let copilotService: CopilotService | null = createCopilotService();
  // Novo estado para loading de espera de troca de contexto
  const isWaitingPreviousStream = ref(false);
  // Carregar mensagens do IndexedDB ao iniciar
  onMounted(async () => {
    openChat();
  });

  onBeforeUnmount(() => {
    copilotService = null;
  });

  // refs para scroll
  const chatMain = ref<HTMLElement | null>(instance.$refs.chatMain);
  const isUserScrolling = ref(false);
  const loadingMsg = ref('');

  // Estado do input e textarea
  const input = ref('');
  const textareaRef = ref<HTMLTextAreaElement | null>(null);
  const textareaRows = ref(1);

  // Novo: resposta crua da listagem de threads
  const threadsResponse = ref<any | null>(null);

  const openChat = async (_thread_id = threadId) => {
    try {
      isOpen.value = true;
      // Apenas chama a listagem de threads e guarda o retorno cru
      const resp = await copilotService?.listThreads({
        user_email,
        agent_id: agentId,
        requester_id: requester_id,
        reference_id,
        page: 1,
        page_size: 1000,
      });
      threadsResponse.value = resp || null;
      return resp;
    } catch (e) {
      threadsResponse.value = null;
      return null;
    }
  };

  // Abre uma thread específica: busca histórico e carrega mensagens
  const openThread = async (thread_id: string) => {
    try {
      isOpen.value = true;
      currentThreadId.value = thread_id;
      threadId = thread_id as any;
      await copilotService?.getThreadHistory(thread_id, user_email, { page_size: 150, agent_id: agentId, requester_id: requester_id });
      const fromDb = await getMessages(thread_id);
      messages.value = Array.isArray(fromDb) ? fromDb : [];
      // Garante que o scroll aconteça no final da conversa após o DOM estar renderizado
      nextTick(() => {
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      });
    } catch (e) {
      messages.value = [];
    }
  };

  const closeChat = () => {
    isOpen.value = false;
  };

  const toggleChat = () => {
    isOpen.value = !isOpen.value;
  };

  const sendMessage = async (content: string) => {
    if (!content.trim()) return;
    loading.value = true;
    messages.value.push({
      id: Math.random().toString(36).substr(2, 9),
      content,
      sender: 'user',
      timestamp: new Date(),
    });
    await nextTick();
    setTimeout(() => {
      scrollToBottom();
    }, 30);
    try {
      if (stream) await sendMessageToCopilot(content);
      else await sendMessageToCopilotNotStream(content);

      if (!stream) {
        const saved = await getMessages(threadId);
        if (saved && saved.length) messages.value = saved;
      }
    } catch (e) {
      error.value = 'Erro ao enviar mensagem';
    } finally {
      loading.value = false;
    }
  };

  const sendMessageToCopilotNotStream = async (content: string) => {
    const data = await copilotService?.createChatNotStream(threadId, {
      user_email,
      requester_input: content,
      requester_id,
      thread_id: threadId || undefined,
      agent_id: agentId || undefined,
      reference_id,
    });
    if (!data) return;
    const { ai_response, internal_id, source, thread_id } = data;
    threadId = thread_id;
    messages.value.push({
      id: internal_id,
      thread_id,
      content: ai_response,
      sender: 'assistant',
      source,
      timestamp: new Date(),
    });
  };

  const sendMessageToCopilot = async (content: string) => {
    const chatDTO = {
      user_email,
      requester_input: content,
      requester_id,
      thread_id: threadId || undefined,
      agent_id: agentId || undefined,
      reference_id,
    }
    if (!chatDTO) return;
    messages.value.push({
      id: null,
      thread_id: undefined,
      content: '',
      sender: 'assistant',
      source: [],
      timestamp: new Date(),
    } as any);
    loadingSendingMessage.value = true;
    const index = messages.value.length - 1;
    const data = await copilotService?.createChat(threadId, chatDTO,
    (msgContent: any) => {
      loading.value = false;
      if (typeof msgContent === 'string') {
        messages.value[index].content = msgContent;
      } else if (msgContent && typeof msgContent === 'object') {
        // Payload final inclui content/source
        if (typeof msgContent.content === 'string') {
          messages.value[index].content = msgContent.content;
        }
        if (msgContent.source) {
          messages.value[index].source = msgContent.source as any;
        }
        if (msgContent.internal_id) {
          messages.value[index].id = msgContent.internal_id;
        }
        if (msgContent.thread_id) {
          messages.value[index].thread_id = msgContent.thread_id;
        }
      }
      instance.$nextTick(() => {
        instance.$refs.chatMain.scrollTop = instance.$refs.chatMain.scrollHeight;
      });
    });
    loadingSendingMessage.value = false;
    const { thread_id, source, internal_id, response_feedback } = data || {};
    if (!data || !thread_id) return;
    threadId = thread_id;
    messages.value[index].id = internal_id;
    messages.value[index].source = source;
    messages.value[index].thread_id = thread_id;
    messages.value[index].response_feedback = response_feedback;

    // Persist the final pair (user + assistant) to IndexedDB immediately
    await saveMessages(threadId, [
      {
        id: internal_id,
        thread_id: threadId,
        content,
        sender: 'user',
        timestamp: new Date(),
      } as TMessage,
      {
        id: internal_id,
        thread_id: threadId,
        content: messages.value[index].content,
        sender: 'assistant',
        source: source,
        response_feedback: response_feedback,
        timestamp: new Date(),
      } as TMessage,
    ]);
  };

  /* ======================== ACTIONS ======================== */
  const likeMessage = async (msg: TMessage) => {
    await copilotService?.feedback(msg.id, threadId, true);
  };

  const dislikeMessage = async (msg: TMessage) => {
    await copilotService?.feedback(msg.id, threadId, false);
  };

  const handleSend = () => {
    if (!input.value.trim()) return;
    sendMessage(input.value);
    loadingMsg.value = input.value;
    input.value = '';
    if (textareaRef.value) {
      textareaRef.value.style.height = 'auto';
    }
  };

  const scrollToBottom = () => {
    if (chatMain.value) {
      chatMain.value.scrollTop = chatMain.value.scrollHeight;
    }
  };

  const onScroll = () => {
    if (!chatMain.value) return;
    const { scrollTop, scrollHeight, clientHeight } = chatMain.value;
    isUserScrolling.value = scrollTop + clientHeight < scrollHeight - 10;
  };

  // ======================== Comunicação por evento ========================

  communication.onReferenceId( async (reference_id: string) => {
    getChatByReferenceId(reference_id);
  });

  communication.onClearChat(async () => {
    messages.value = [];
    threadId = undefined as unknown as string;
    currentThreadId.value = undefined;
    clearAllMessages();
  });

  const getChatByReferenceId = async (_reference_id: string) => {
    isWaitingPreviousStream.value = loadingSendingMessage.value;
    try {
      const thread_id = await copilotService?.getChatByReferenceId(requester_id, _reference_id);
      reference_id = _reference_id;
      threadId = thread_id;
      currentThreadId.value = thread_id;
      openChat(threadId);
    } catch (err) {
      reference_id = _reference_id;
      threadId = undefined as unknown as string;
      messages.value = [];
    } finally {
      isWaitingPreviousStream.value = false;
      loadingSendingMessage.value = false;
    }
  };

  const sendDetailedFeedback = async (internal_id: string, descritive_feedback: string, feedback_tags: string[]) => {
    try {
      await copilotService?.detailedFeedback(internal_id, descritive_feedback, feedback_tags);
    } catch (err) {
      console.error(err);
    }
  };

  // Função customizada para copiar mensagens convertendo HTML para formato WhatsApp
  const copyMessage = async (content: string) => {
    // Converte o HTML renderizado de volta para formato WhatsApp
    const htmlRendered = renderMarkdown(content);
    const whatsappFormat = htmlToWhatsApp(htmlRendered);
    await copyToClipboard(whatsappFormat);
  };

  const refreshThreads = async () => {
    try {
      const resp = await copilotService?.listThreads({
        user_email,
        agent_id: agentId,
        requester_id,
        reference_id,
        page: 1,
        page_size: 1000,
      });
      threadsResponse.value = resp || null;
      return resp;
    } catch (e) {
      threadsResponse.value = null;
      return null;
    }
  };

  return {
    isOpen: computed(() => isOpen.value),
    messages: computed(() => messages.value),
    loading: computed(() => loading.value),
    loadingSendingMessage: computed(() => loadingSendingMessage.value),
    error: computed(() => error.value),
    openChat,
    openThread,
    closeChat,
    toggleChat,
    sendMessage,
    chatMain,
    isUserScrolling,
    scrollToBottom,
    onScroll,
    copyMessage,
    likeMessage,
    dislikeMessage,
    // input e textarea
    input,
    textareaRef,
    textareaRows,
    handleSend,
    loadingMsg,
    // notification
    showNotification: computed(() => notification.showNotification.value),
    notificationMessage: computed(() => notification.notificationMessage.value),
    notificationType: computed(() => notification.notificationType.value),
    renderMarkdown,
    isWaitingPreviousStream,
    sendDetailedFeedback,
    refreshThreads,
    // current thread ID
    currentThreadId: computed(() => currentThreadId.value),
    // threads raw response
    threadsResponse: computed(() => threadsResponse.value),
  };
};
